import { useState, useEffect } from 'react';
import { MapPin, ArrowRight, Globe, Flag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DestinationsPage = () => {
  const [destinations, setDestinations] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.get(`${API}/destinations`);
        setDestinations(response.data);
      } catch (error) {
        setDestinations([
          { 
            id: '1', 
            name: 'Silicon Valley', 
            country: 'Estados Unidos', 
            description: 'Visita Google, Apple, Meta y las startups más innovadoras del mundo. Experiencia de tecnología e innovación.', 
            image_url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800', 
            type: 'internacional',
            carrera: 'Tecnología & Negocios'
          },
          { 
            id: '2', 
            name: 'Ruta Imperial de la Medicina', 
            country: 'Viena - Berlín - Praga - Budapest', 
            description: 'Hospitales universitarios de clase mundial, institutos de investigación médica y la cuna de la medicina moderna europea.', 
            image_url: 'https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800', 
            type: 'internacional',
            carrera: 'Medicina & Salud'
          },
          { 
            id: '3', 
            name: 'China - Negocios Internacionales', 
            country: 'Beijing - Shanghai', 
            description: 'Descubre el gigante asiático: zonas económicas especiales, empresas tecnológicas líderes y cultura milenaria.', 
            image_url: 'https://images.unsplash.com/photo-1474181487882-5abf3f0ba6c2?w=800', 
            type: 'internacional',
            carrera: 'Negocios Internacionales'
          },
          { 
            id: '4', 
            name: 'Planta BMW San Luis Potosí', 
            country: 'México', 
            description: 'Visita la planta de manufactura más moderna de Latinoamérica. Ingeniería automotriz de clase mundial.', 
            image_url: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800', 
            type: 'nacional',
            carrera: 'Ingeniería'
          },
          { 
            id: '5', 
            name: 'CDMX - Ruta Finanzas', 
            country: 'México', 
            description: 'Bolsa Mexicana de Valores, Corporativos Financieros y Museos del área económica financiera.', 
            image_url: 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?w=800', 
            type: 'nacional',
            carrera: 'Finanzas & Negocios'
          },
          { 
            id: '6', 
            name: 'Guadalajara - Congreso Pixel Art', 
            country: 'México', 
            description: 'El evento más importante de diseño digital y animación en Latinoamérica. Networking con la industria creativa.', 
            image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800', 
            type: 'nacional',
            carrera: 'Diseño & Arte Digital'
          },
          { 
            id: '7', 
            name: 'Querétaro - Clusters Industriales', 
            country: 'México', 
            description: 'Recorrido por los parques industriales aeroespaciales y automotrices más importantes del país.', 
            image_url: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800', 
            type: 'nacional',
            carrera: 'Ingeniería Industrial'
          },
          { 
            id: '8', 
            name: 'Nueva York - Wall Street', 
            country: 'Estados Unidos', 
            description: 'Visita la Bolsa de Nueva York, bancos de inversión y las principales instituciones financieras del mundo.', 
            image_url: 'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=800', 
            type: 'internacional',
            carrera: 'Finanzas'
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, []);

  const filteredDestinations = activeFilter === 'all' ? destinations : destinations.filter((d) => d.type === activeFilter);

  const filters = [
    { id: 'all', label: 'Todos', icon: Globe },
    { id: 'nacional', label: 'Nacionales', icon: Flag },
    { id: 'internacional', label: 'Internacionales', icon: MapPin },
  ];

  return (
    <main data-testid="destinations-page" className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1920&q=80"
            alt="Destinations"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/70 to-[#1A1A1A]/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="inline-block text-[#009DE0] font-semibold text-sm uppercase tracking-wider mb-4">
                Destinos
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Explora el <span className="text-[#009DE0]">mundo</span>
              </h1>
            </div>

            {/* Filters */}
            <div className="flex gap-3 mt-8 lg:mt-0">
              {filters.map((filter) => {
                const IconComponent = filter.icon;
                return (
                  <button
                    key={filter.id}
                    data-testid={`filter-${filter.id}`}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                      activeFilter === filter.id
                        ? 'bg-[#009DE0] text-white'
                        : 'bg-white/10 backdrop-blur-sm text-white/70 hover:bg-white/20 hover:text-white'
                    }`}
                  >
                    <IconComponent size={16} />
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-2 border-[#009DE0] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredDestinations.map((destination) => (
                <a
                  key={destination.id}
                  href={`https://wa.me/522282728556?text=${encodeURIComponent(`Hola, me interesa cotizar el viaje a ${destination.name}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`destination-card-${destination.id}`}
                  className="group relative h-96 rounded-2xl overflow-hidden cursor-pointer"
                >
                  {/* Image */}
                  <img
                    src={destination.image_url}
                    alt={destination.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/95 via-[#1A1A1A]/40 to-transparent" />

                  {/* Type Badge */}
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    <span
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                        destination.type === 'internacional'
                          ? 'bg-[#009DE0] text-white'
                          : 'bg-white text-[#1A1A1A]'
                      }`}
                    >
                      {destination.type}
                    </span>
                    {destination.carrera && (
                      <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#1A1A1A]/80 text-white backdrop-blur-sm">
                        {destination.carrera}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
                      <MapPin size={14} />
                      {destination.country}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {destination.name}
                    </h3>
                    <p className="text-white/70 text-sm mb-4 line-clamp-2">
                      {destination.description}
                    </p>
                    <div className="flex items-center gap-2 text-[#009DE0] font-semibold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span>Cotizar por WhatsApp</span>
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">
            ¿No encuentras tu destino ideal?
          </h2>
          <p className="text-[#1A1A1A]/60 text-lg mb-10">
            Diseñamos viajes a medida. Cuéntanos a dónde quieres ir.
          </p>
          <a
            href="https://wa.me/522282728556?text=Hola,%20me%20interesa%20cotizar%20un%20viaje%20a%20un%20destino%20personalizado"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#009DE0] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#007BB0] transition-all"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Solicitar Destino Personalizado
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </main>
  );
};

export default DestinationsPage;
